// Thin fetch wrapper used by every authenticated API call from the SPA.
// Centralizes:
//   1. The API origin (single source of truth so the auth and capture
//      sub-paths can both be derived from one env var).
//   2. Bearer-token attachment from authStorage.
//   3. 401 handling — clears local auth and dispatches a window event so
//      AuthContext can react without apiClient depending on React.
//   4. JSON body / response parsing.
//
// Kept intentionally small. No retry, no interceptor stack, no caching.

import { clearStoredAuth, getStoredAuth } from "../utils/authStorage";
import { AUTH_EXPIRED_EVENT } from "../contexts/AuthContext";

// When REACT_APP_API_ORIGIN is set (even to ""), use that value.
// Empty string means "same origin" — which is what we want for local dev
// with CRA's proxy (package.json "proxy" → localhost:8001).
// Only fall back to the production origin when the env var is undefined.
export const API_ORIGIN =
  process.env.REACT_APP_API_ORIGIN !== undefined
    ? process.env.REACT_APP_API_ORIGIN
    : "https://api.capcam.cn";

// Feed/capture services may run on a separate origin in local dev.
// Falls back to API_ORIGIN when not set.
export const FEED_ORIGIN =
  process.env.REACT_APP_FEED_ORIGIN !== undefined
    ? process.env.REACT_APP_FEED_ORIGIN
    : API_ORIGIN;

// Auth service — separate upstream, no service prefix.
export const AUTH_API_BASE = `${API_ORIGIN}/api`;

// Capture-management service. The `/capture-management` prefix is a prod
// Caddy routing hook, stripped at the edge before the request reaches the
// backend; src/setupProxy.js mirrors that strip for local dev.
export const FEED_API_BASE = `${FEED_ORIGIN}/capture-management/api`;

export interface ApiFetchOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  body?: unknown;
  // When true, attach Authorization: Bearer <token> from authStorage.
  // 401 responses always clear the token regardless of this flag.
  auth?: boolean;
  // Override the base URL. Defaults to AUTH_API_BASE.
  base?: string;
  // Extra headers merged on top of the defaults.
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export class ApiError extends Error {
  status: number;
  payload: unknown;
  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

const dispatchAuthExpired = (): void => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
};

export const apiFetch = async <T = unknown>(
  path: string,
  options: ApiFetchOptions = {}
): Promise<T> => {
  const base = options.base ?? AUTH_API_BASE;
  const url = `${base}${path}`;

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(options.headers || {}),
  };

  let body: BodyInit | undefined;
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.body);
  }

  if (options.auth) {
    const stored = getStoredAuth();
    if (stored?.token) {
      headers.Authorization = `Bearer ${stored.token}`;
    }
  }

  const response = await fetch(url, {
    method: options.method ?? (body ? "POST" : "GET"),
    headers,
    body,
    signal: options.signal,
  });

  // 401 always clears auth, even on unauthenticated calls — the server is
  // telling us our token is no good.
  if (response.status === 401) {
    clearStoredAuth();
    dispatchAuthExpired();
  }

  let payload: unknown = null;
  const text = await response.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!response.ok) {
    throw new ApiError(
      `Request failed: ${response.status}`,
      response.status,
      payload
    );
  }

  return payload as T;
};
